import React, {
  useState,
  useContext,
  useRef,
  useMemo,
  useEffect,
} from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CartContext } from "../context/CartContext";

function getNailDimensions(length) {
  // Slimmer nails so they don’t look thicker than fingers
  const baseWidth = 16;
  const baseHeight = 42;
  const scale =
    length === "Short" ? 0.8 : length === "Long" ? 1.25 : 1.0;

  return {
    width: baseWidth,
    height: baseHeight * scale,
  };
}

export default function Designer() {
  const [shape, setShape] = useState("Square");
  const [length, setLength] = useState("Medium");
  const [color, setColor] = useState("Beige");
  const [charms, setCharms] = useState([]); // {id, src, x, y, size, rotation}
  const [selected, setSelected] = useState(null); // { type: 'nail' | 'charm', id }
  const { addToCart } = useContext(CartContext);

  const boardRef = useRef(null);
  const draggingCharmIdRef = useRef(null);
  const draggingNailIdRef = useRef(null);

  // Nail size depends on length
  const { width: nailWidth, height: nailHeight } = getNailDimensions(length);

  // Nail color from selection
  const nailColor =
    color === "Black"
      ? "#111827"
      : color === "Blue"
      ? "#1D4ED8"
      : "#E6D0B3"; // Beige

  const basePrice = useMemo(() => {
    let total = 20;
    if (length === "Medium") total += 5;
    if (length === "Long") total += 10;
    if (color !== "Beige") total += 3;
    return total;
  }, [length, color]);

  const price = basePrice + charms.length * 2; // $2 per charm

  const shapes = ["Oval", "Square"];
  const lengths = ["Short", "Medium", "Long"];
  const colors = ["Black", "Blue", "Beige"];

  const charmCatalog = [
    {
      id: "heart",
      src: `${process.env.PUBLIC_URL}/charms/heart.png`,
      label: "Heart",
    },
    {
      id: "star",
      src: `${process.env.PUBLIC_URL}/charms/star.png`,
      label: "Star",
    },
    {
      id: "flower",
      src: `${process.env.PUBLIC_URL}/charms/flower.png`,
      label: "Flower",
    },
    {
      id: "pearl",
      src: `${process.env.PUBLIC_URL}/charms/pearl.png`,
      label: "Pearl",
    },
  ];

  // Smaller charms so they fit nicely on nails
  const charmSize =
    length === "Short" ? 16 : length === "Medium" ? 18 : 20;

  // Resize existing charms when length changes
  useEffect(() => {
    setCharms((prev) => prev.map((c) => ({ ...c, size: charmSize })));
  }, [charmSize]);

  // 🧷 Nails: movable rectangles with rotation over the model
  const [nails, setNails] = useState(() => {
    // Initial positions (rough) in board pixels (for a 640x500 board)
    return [
      // LEFT HAND (thumb → pinky)
      { id: "left-thumb", label: "Left Thumb", x: 170, y: 300, rotation: 0 },
      { id: "left-index", label: "Left Index", x: 220, y: 225, rotation: 0 },
      { id: "left-middle", label: "Left Middle", x: 265, y: 205, rotation: 0 },
      { id: "left-ring", label: "Left Ring", x: 310, y: 215, rotation: 0 },
      { id: "left-pinky", label: "Left Pinky", x: 350, y: 245, rotation: 0 },

      // RIGHT HAND (thumb → pinky)
      { id: "right-thumb", label: "Right Thumb", x: 470, y: 300, rotation: 0 },
      { id: "right-index", label: "Right Index", x: 420, y: 225, rotation: 0 },
      { id: "right-middle", label: "Right Middle", x: 375, y: 205, rotation: 0 },
      { id: "right-ring", label: "Right Ring", x: 330, y: 215, rotation: 0 },
      { id: "right-pinky", label: "Right Pinky", x: 290, y: 245, rotation: 0 },
    ].map((n) => ({ ...n, width: nailWidth, height: nailHeight }));
  });

  // Update nail size when length changes (keep positions + rotation)
  useEffect(() => {
    setNails((prev) =>
      prev.map((n) => ({
        ...n,
        width: nailWidth,
        height: nailHeight,
      }))
    );
  }, [nailWidth, nailHeight]);

  // Add charm by dropping from catalog
  const handleBoardDrop = (e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("text/plain");
    const found = charmCatalog.find((c) => c.id === data);
    if (!found || !boardRef.current) return;

    const rect = boardRef.current.getBoundingClientRect();
    // Store top-left, but place under cursor center
    const x = e.clientX - rect.left - charmSize / 2;
    const y = e.clientY - rect.top - charmSize / 2;

    setCharms((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        src: found.src,
        x,
        y,
        size: charmSize,
        rotation: 0,
      },
    ]);
  };

  const handleDragOver = (e) => e.preventDefault();

  // Clicking empty board: clear selection
  const handleBoardPointerDown = () => {
    setSelected(null);
  };

  // Start dragging a charm + select it
  const onCharmPointerDown = (id) => (e) => {
    e.stopPropagation(); // prevent board from clearing selection
    draggingCharmIdRef.current = id;
    setSelected({ type: "charm", id });
    if (e.pointerId != null && e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  // Start dragging a nail + select it
  const onNailPointerDown = (id) => (e) => {
    e.stopPropagation(); // prevent board from clearing selection
    draggingNailIdRef.current = id;
    setSelected({ type: "nail", id });
    if (e.pointerId != null && e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  // Move whichever is being dragged
  const onBoardPointerMove = (e) => {
    if (!boardRef.current) return;

    const rect = boardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const nailId = draggingNailIdRef.current;
    const charmId = draggingCharmIdRef.current;

    if (nailId) {
      setNails((prev) =>
        prev.map((n) => (n.id === nailId ? { ...n, x, y } : n))
      );
    } else if (charmId) {
      setCharms((prev) =>
        prev.map((c) =>
          c.id === charmId
            ? { ...c, x: x - c.size / 2, y: y - c.size / 2 }
            : c
        )
      );
    }
  };

  const onBoardPointerUp = () => {
    draggingCharmIdRef.current = null;
    draggingNailIdRef.current = null;
  };

  const removeCharm = (id) =>
    setCharms((prev) => prev.filter((c) => c.id !== id));

  const handleAddToCart = () => {
    addToCart({ shape, length, color, price, charms, nails });
    alert("Added to cart!");
  };

  // Figure out what is currently selected for the angle slider
  let selectedLabel = null;
  let selectedRotation = 0;

  if (selected?.type === "nail") {
    const n = nails.find((x) => x.id === selected.id);
    if (n) {
      selectedLabel = n.label;
      selectedRotation = n.rotation;
    }
  } else if (selected?.type === "charm") {
    const idx = charms.findIndex((c) => c.id === selected.id);
    if (idx !== -1) {
      selectedLabel = `Charm ${idx + 1}`;
      selectedRotation = charms[idx].rotation || 0;
    }
  }

  const handleRotationChange = (e) => {
    const angle = Number(e.target.value);
    if (!selected) return;

    if (selected.type === "nail") {
      setNails((prev) =>
        prev.map((n) =>
          n.id === selected.id ? { ...n, rotation: angle } : n
        )
      );
    } else if (selected.type === "charm") {
      setCharms((prev) =>
        prev.map((c) =>
          c.id === selected.id ? { ...c, rotation: angle } : c
        )
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-white bg-gradient-to-b from-gray-900 via-purple-900 to-black">
      <Navbar />
      <div className="flex-grow relative z-10 flex flex-col items-center justify-start pt-20 px-6">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-8 shadow-lg w-full max-w-6xl flex flex-col md:flex-row gap-10">
          {/* LEFT: Preview */}
          <div className="flex flex-col items-center justify-center flex-1">
            <h3 className="text-lg font-semibold mb-4">Preview</h3>

            <div
              ref={boardRef}
              className="relative bg-black/30 rounded-lg p-6 shadow-lg w-[640px] h-[500px] overflow-hidden"
              onDrop={handleBoardDrop}
              onDragOver={handleDragOver}
              onPointerDown={handleBoardPointerDown}
              onPointerMove={onBoardPointerMove}
              onPointerUp={onBoardPointerUp}
            >
              {/* Bigger hand model as background */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <img
                  src="/models/pink short square left.svg"
                  alt="Hand model"
                  className="w-[125%] h-[125%] object-contain"
                  style={{ pointerEvents: "none" }}
                />
              </div>

              {/* Draggable, rotatable nails */}
              {nails.map((n) => (
                <div
                  key={n.id}
                  className="absolute cursor-grab"
                  style={{
                    left: n.x,
                    top: n.y,
                    width: n.width,
                    height: n.height,
                    backgroundColor: nailColor,
                    transform: `translate(-50%, -50%) rotate(${n.rotation}deg)`,
                    borderRadius:
                      shape === "Oval"
                        ? `${Math.max(n.width, n.height)}px` // fully oval
                        : "2px", // almost straight, clearly square
                    boxShadow:
                      selected?.type === "nail" && selected.id === n.id
                        ? "0 0 0 2px rgba(236,72,153,0.9)"
                        : "0 0 0 1px rgba(0,0,0,0.35)",
                    pointerEvents: "auto",
                    transition: "box-shadow 0.1s ease",
                  }}
                  onPointerDown={onNailPointerDown(n.id)}
                  title="Drag to move nail • Click to select"
                />
              ))}

              {/* Draggable, rotatable charms */}
              {charms.map((c) => (
                <img
                  key={c.id}
                  src={c.src}
                  alt="charm"
                  className="absolute cursor-grab select-none"
                  style={{
                    left: c.x,
                    top: c.y,
                    width: c.size,
                    height: c.size,
                    userSelect: "none",
                    pointerEvents: "auto",
                    transform: `rotate(${c.rotation || 0}deg)`,
                    transformOrigin: "center center",
                    boxShadow:
                      selected?.type === "charm" && selected.id === c.id
                        ? "0 0 0 2px rgba(236,72,153,0.9)"
                        : "none",
                    borderRadius: "999px",
                    backgroundColor:
                      selected?.type === "charm" && selected.id === c.id
                        ? "rgba(255,255,255,0.9)"
                        : "transparent",
                  }}
                  draggable={false}
                  onPointerDown={onCharmPointerDown(c.id)}
                  onDoubleClick={() => removeCharm(c.id)}
                  title="Drag to move • Click to select • Double-click to remove"
                />
              ))}
            </div>

            {/* Angle setter for selected nail or charm */}
            <div className="mt-4 w-full max-w-xs text-center">
              {selectedLabel != null ? (
                <>
                  <p className="text-sm mb-1">
                    <span className="font-semibold">
                      {selectedLabel}
                    </span>{" "}
                    angle:{" "}
                    <span className="text-pink-300">
                      {selectedRotation}°
                    </span>
                  </p>
                  <input
                    type="range"
                    min={-90}
                    max={90}
                    step={1}
                    value={selectedRotation}
                    onChange={handleRotationChange}
                    className="w-full"
                  />
                  <p className="text-xs text-white/60 mt-1">
                    Drag to move • use the slider to rotate.
                  </p>
                </>
              ) : (
                <p className="text-xs text-white/60">
                  Click a nail or charm to select it and adjust its angle.
                </p>
              )}
            </div>

            <p className="mt-4 text-xl font-bold text-pink-400">
              Total: ${price}
            </p>
            <button
              onClick={handleAddToCart}
              className="mt-3 px-6 py-3 bg-pink-500 text-white rounded-lg shadow-lg hover:bg-pink-600 transition"
            >
              Add to Cart
            </button>
            <p className="text-xs text-white/60 mt-2">
              Tip: drag nails into position, then drag charms onto them.
              Double-click a charm to remove it.
            </p>
          </div>

          {/* RIGHT: Controls */}
          <div className="flex-1 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Shape</h3>
              <div className="flex gap-3">
                {shapes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setShape(s)}
                    className={`px-4 py-2 rounded-lg ${
                      shape === s
                        ? "bg-pink-500 text-white"
                        : "bg-white/20 text-white hover:bg-white/30"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Length</h3>
              <div className="flex gap-3">
                {lengths.map((l) => (
                  <button
                    key={l}
                    onClick={() => setLength(l)}
                    className={`px-4 py-2 rounded-lg ${
                      length === l
                        ? "bg-pink-500 text-white"
                        : "bg-white/20 text-white hover:bg-white/30"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Color</h3>
              <div className="flex gap-3">
                {colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`px-4 py-2 rounded-lg ${
                      color === c
                        ? "bg-pink-500 text-white"
                        : "bg-white/20 text-white hover:bg-white/30"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">
                Charms (drag onto the nails)
              </h3>
              <div className="flex gap-4 flex-wrap">
                {charmCatalog.map((c) => (
                  <div key={c.id} className="flex flex-col items-center">
                    <img
                      src={c.src}
                      alt={c.label}
                      width={40}
                      height={40}
                      draggable
                      onDragStart={(e) =>
                        e.dataTransfer.setData("text/plain", c.id)
                      }
                      className="bg-white rounded-md p-2 cursor-grab"
                    />
                    <span className="text-xs mt-1">{c.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-white/70 mt-2">
                +$2 per charm added
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
